import React, { Suspense, lazy, useEffect } from "react";
import { Router, Switch, Route, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { HeadProvider } from "react-head";
import { parse } from "query-string";

import { CourtProvider } from "../../contexts/court";
import { DocketProvider } from "../../contexts/dockets";
import { LandingStylingProvider } from "../../contexts/landingStyling";
import { SearchProvider } from "../../contexts/search";
import { UserProvider } from "../../contexts/user";
import { LitelyticsAppDiv } from "./LitelyticsAppDiv";
import { LexisHistoryProvider } from "../../contexts/lexisHistory";
import { PdfExportProvider } from "contexts/pdfExport";
import FeatureFlagProvider from "../../contexts/FeatureFlagProvider";

import "./App.scss";
import "animate.css";

import OhDear from "./OhDear";
import NotFound from "./NotFound";
import LexisNexisPDFTitlePage from "./LexisNexisPDFTitlePage";

const JudgeProvider = lazy(() =>
  import("contexts/judge").then((module) => ({
    default: module.JudgeProvider,
  })),
);
const AttorneyProvider = lazy(() =>
  import("contexts/attorney").then((module) => ({
    default: module.AttorneyProvider,
  })),
);
const LawFirmProvider = lazy(() =>
  import("contexts/lawFirm").then((module) => ({
    default: module.LawFirmProvider,
  })),
);
const PartyProvider = lazy(() =>
  import("contexts/party").then((module) => ({
    default: module.PartyProvider,
  })),
);

const Landing = lazy(() => import("../pages/landing/Landing"));
const Results = lazy(() => import("../pages/results/Results"));
const CourtsJudgesCompare = lazy(() =>
  import("../pages/tools/CourtsJudgesCompare/CourtsJudgesCompare"),
);
const CounselCompare = lazy(() =>
  import("../pages/tools/CounselCompare/CounselCompare"),
);
const PartyGroupEditor = lazy(() =>
  import("../pages/tools/PartyGroupEditor/PartyGroupEditor"),
);
const EarlyCaseAssessor = lazy(() =>
  import("../pages/tools/EarlyCaseAssessor"),
);
const Attorney = lazy(() => import("../pages/entity/Attorney"));
const Court = lazy(() => import("../pages/entity/Court"));
const Judge = lazy(() => import("../pages/entity/Judge"));
const LawFirm = lazy(() => import("../pages/entity/LawFirm"));
const Parties = lazy(() => import("../pages/entity/Parties"));
const NonLXMStateDockets = lazy(() =>
  import("../pages/dockets/NonLXMStateDockets"),
);

const CourtCoveragePage = lazy(() =>
  import("components/pages/help/CourtCoveragePage"),
);
const WhatsABoxPlot = lazy(() => import("../pages/help/WhatsABoxPlot"));

const SentryRoute = Sentry.withSentryRouting(Route);

export function GlobalProviders({ children }) {
  return (
    <HeadProvider>
      <FeatureFlagProvider>
        <UserProvider>
          <PdfExportProvider>
            <LandingStylingProvider>
              <LexisHistoryProvider>
                <SearchProvider>{children}</SearchProvider>
              </LexisHistoryProvider>
            </LandingStylingProvider>
          </PdfExportProvider>
        </UserProvider>
      </FeatureFlagProvider>
    </HeadProvider>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App(props) {
  const mountPath = props.mountPath;
  const history = props.history;
  return (
    // Outer most ErrorBoundery to capture and report anything that goes
    // wrong without being handled in the specific component it happened in.
    <Sentry.ErrorBoundary fallback={OhDear} showDialog>
      <Router basename={mountPath} history={history}>
        <ScrollToTop />
        <GlobalProviders>
          <LitelyticsAppDiv>
            <Suspense fallback={<div>Loading...</div>}>
              <LexisNexisPDFTitlePage />
              <Switch>
                <SentryRoute exact path="/" component={Landing} />
                <SentryRoute exact path="/results" component={Results} />
                <SentryRoute
                  path="/court-judge-compare"
                  component={CourtsJudgesCompare}
                />
                <SentryRoute
                  path="/counsel-compare"
                  component={CounselCompare}
                />
                <SentryRoute
                  path="/party-group-editor"
                  component={PartyGroupEditor}
                />
                <SentryRoute
                  path="/early-case-assessor"
                  component={EarlyCaseAssessor}
                />
                <SentryRoute
                  path="/attorney/:id"
                  render={(routeProps) => (
                    <AttorneyProvider attorneyId={routeProps.match.params.id}>
                      <Attorney />
                    </AttorneyProvider>
                  )}
                />
                <SentryRoute
                  path="/court/:id+"
                  render={(routeProps) => (
                    <CourtProvider courtId={routeProps.match.params.id}>
                      <Court />
                    </CourtProvider>
                  )}
                />
                <SentryRoute
                  path="/judge/:id"
                  render={(routeProps) => (
                    <JudgeProvider judgeId={routeProps.match.params.id}>
                      <Judge />
                    </JudgeProvider>
                  )}
                />
                <SentryRoute
                  path="/law_firm/:id"
                  render={(routeProps) => (
                    <LawFirmProvider lawFirmId={routeProps.match.params.id}>
                      <LawFirm />
                    </LawFirmProvider>
                  )}
                />
                <SentryRoute
                  path="/party"
                  render={(routeProps) => {
                    const urlParams = new URLSearchParams(
                      routeProps.location.search,
                    );
                    let partyIds = urlParams.getAll("id");

                    // Handle case where Lexis+ converts multiple id params to comma-separated values
                    // e.g., ?id=1286,64593421,23007277,52824279
                    if (partyIds.length === 1 && partyIds[0].includes(",")) {
                      partyIds = partyIds[0].split(",").map((id) => id.trim());
                    }

                    return (
                      <PartyProvider partyIds={partyIds}>
                        <Parties />
                      </PartyProvider>
                    );
                  }}
                />
                <SentryRoute
                  path="/dockets/court/:id+"
                  render={(routeProps) => (
                    <CourtProvider courtId={routeProps.match.params.id}>
                      <DocketProvider
                        entityId={routeProps.match.params.id}
                        entityType="court"
                      >
                        <NonLXMStateDockets entityType="court" />
                      </DocketProvider>
                    </CourtProvider>
                  )}
                />
                <SentryRoute
                  path="/dockets/judge/:id+"
                  render={(routeProps) => (
                    <JudgeProvider judgeId={routeProps.match.params.id}>
                      <DocketProvider
                        entityId={routeProps.match.params.id}
                        entityType="judge"
                      >
                        <NonLXMStateDockets entityType="judge" />
                      </DocketProvider>
                    </JudgeProvider>
                  )}
                />
                <SentryRoute
                  path="/dockets/attorney/:id+"
                  render={(routeProps) => (
                    <AttorneyProvider attorneyId={routeProps.match.params.id}>
                      <CourtProvider
                        courtId={parse(routeProps.location.search).court}
                      >
                        <DocketProvider
                          entityId={routeProps.match.params.id}
                          entityType="attorney"
                        >
                          <NonLXMStateDockets entityType="attorney" />
                        </DocketProvider>
                      </CourtProvider>
                    </AttorneyProvider>
                  )}
                />
                <SentryRoute
                  path="/dockets/law_firm/:id+"
                  render={(routeProps) => (
                    <LawFirmProvider lawFirmId={routeProps.match.params.id}>
                      <CourtProvider
                        courtId={parse(routeProps.location.search).court}
                      >
                        <DocketProvider
                          entityId={routeProps.match.params.id}
                          entityType="law_firm"
                        >
                          <NonLXMStateDockets entityType="law_firm" />
                        </DocketProvider>
                      </CourtProvider>
                    </LawFirmProvider>
                  )}
                />
                <SentryRoute
                  exact
                  path="/help/boxplot"
                  component={WhatsABoxPlot}
                />
                <SentryRoute
                  exact
                  path="/help/court-coverage"
                  component={CourtCoveragePage}
                />
                <Route>
                  <NotFound />
                </Route>
              </Switch>
            </Suspense>
          </LitelyticsAppDiv>
        </GlobalProviders>
      </Router>
    </Sentry.ErrorBoundary>
  );
}

export default App;
